import type { Overview } from '@growth/client';
import { useParams } from 'react-router';
import { ColumnChart, LineChart } from '../components/charts.tsx';
import { FilterBar } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { ChartCard, ErrorBox, StatTile } from '../components/ui.tsx';
import { useGlobalFilters } from '../filters.ts';
import { fmtCompact, fmtDay, fmtDuration, fmtInt, fmtNum, fmtPct } from '../format.ts';
import { useApi } from '../session.tsx';
import { FreshnessLine } from './Overview.tsx';

export function EngagementPage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters();
  const ov = useApi<Overview>(`ov:${gameId}:${filters.key}`, (api, s) => api.overview(gameId, filters.params, s));
  const e = ov.data?.engagement;
  const s = e?.series ?? [];
  const stale = ov.loading && !!ov.data;
  const dates = s.map((p) => p.date);
  const table = {
    columns: [
      { label: 'Día' },
      { label: 'DAU', numeric: true },
      { label: 'WAU', numeric: true },
      { label: 'MAU', numeric: true },
      { label: 'DAU/MAU', numeric: true },
      { label: 'Sesiones', numeric: true },
      { label: 'Juego/DAU', numeric: true },
      { label: 'Runs', numeric: true },
    ],
    rows: s.map((p) => [
      p.date,
      fmtInt(p.dau),
      fmtInt(p.wau),
      fmtInt(p.mau),
      p.mau ? fmtPct(p.dau / p.mau) : '—',
      fmtInt(p.sessions),
      p.dau ? fmtDuration(p.playtime_s / p.dau) : '—',
      fmtInt(p.runs_started),
    ]),
  };

  return (
    <>
      <PageHeader title="Engagement" sub={<FreshnessLine fresh={ov.data?.fresh} />} />
      <FilterBar filters={filters} />
      {ov.error && <ErrorBox error={ov.error} onRetry={ov.reload} />}
      <div className={`kpis loading-frame${stale ? ' stale' : ''}`}>
        <StatTile label="DAU medio" value={fmtCompact(e?.dau_avg)} trend={s.map((p) => p.dau)} />
        <StatTile label="WAU (último día)" value={fmtCompact(e?.wau)} />
        <StatTile label="MAU (último día)" value={fmtCompact(e?.mau)} />
        <StatTile label="DAU/MAU" value={fmtPct(e?.dau_mau)} sub="stickiness media" />
        <StatTile label="Usuarios activos" value={fmtCompact(e?.active_users)} sub="distintos en el periodo" />
        <StatTile label="Usuarios recurrentes" value={fmtCompact(e?.returning_user_days)} sub="usuario-día no nuevos" />
        <StatTile label="Sesiones por DAU" value={fmtNum(e?.sessions_per_dau, 2)} sub={e ? `${fmtInt(e.sessions)} sesiones` : undefined} />
        <StatTile label="Sesión media" value={fmtDuration(e?.avg_session_s)} sub={e ? `mediana ${fmtDuration(e.median_session_s)} · n=${fmtInt(e.sessions_measured)}` : undefined} />
        <StatTile label="Tiempo de juego por DAU" value={fmtDuration(e?.playtime_per_dau_s)} />
        <StatTile label="Runs por DAU" value={fmtNum(e?.runs_per_dau, 2)} sub={e ? `${fmtNum(e.runs_per_player, 2)} por jugador` : undefined} />
        <StatTile label="Duración de run" value={fmtDuration(e?.avg_run_s)} sub={e ? `mediana ${fmtDuration(e.median_run_s)}` : undefined} />
      </div>

      <ChartCard title="DAU, WAU y MAU" subtitle="Usuarios distintos activos en el día, en los últimos 7 y en los últimos 30 días" stale={stale} table={table}>
        <LineChart
          ariaLabel="DAU, WAU y MAU por día"
          x={dates}
          formatX={(v) => fmtDay(String(v))}
          formatY={fmtCompact}
          formatValue={fmtInt}
          series={[
            { key: 'dau', label: 'DAU', slot: 1, values: s.map((p) => p.dau) },
            { key: 'wau', label: 'WAU', slot: 2, values: s.map((p) => p.wau) },
            { key: 'mau', label: 'MAU', slot: 3, values: s.map((p) => p.mau) },
          ]}
        />
      </ChartCard>

      <div className="grid cols-2" style={{ marginTop: 16 }}>
        <ChartCard title="Sesiones por día" stale={stale} table={table}>
          <ColumnChart ariaLabel="Sesiones por día" x={dates} formatX={(v) => fmtDay(String(v))} formatY={fmtCompact} formatValue={fmtInt} series={[{ key: 'sessions', label: 'Sesiones', slot: 1, values: s.map((p) => p.sessions) }]} />
        </ChartCard>
        <ChartCard title="Tiempo de juego por DAU" subtitle="Minutos por usuario activo y día" stale={stale} table={table}>
          <LineChart
            ariaLabel="Tiempo de juego por DAU"
            x={dates}
            formatX={(v) => fmtDay(String(v))}
            formatY={(v) => `${fmtNum(v, 0)} min`}
            formatValue={(v) => fmtDuration(v * 60)}
            series={[{ key: 'pt', label: 'Minutos por DAU', slot: 1, values: s.map((p) => (p.dau ? p.playtime_s / p.dau / 60 : null)) }]}
          />
        </ChartCard>
        <ChartCard title="Runs iniciadas por día" stale={stale} table={table}>
          <ColumnChart ariaLabel="Runs iniciadas por día" x={dates} formatX={(v) => fmtDay(String(v))} formatY={fmtCompact} formatValue={fmtInt} series={[{ key: 'runs', label: 'Runs', slot: 1, values: s.map((p) => p.runs_started) }]} />
        </ChartCard>
        <ChartCard title="Stickiness (DAU/MAU)" stale={stale} table={table}>
          <LineChart
            ariaLabel="DAU/MAU por día"
            x={dates}
            formatX={(v) => fmtDay(String(v))}
            formatY={(v) => fmtPct(v, 0)}
            formatValue={(v) => fmtPct(v)}
            yCap={1}
            series={[{ key: 'stick', label: 'DAU/MAU', slot: 1, values: s.map((p) => (p.mau ? p.dau / p.mau : null)) }]}
          />
        </ChartCard>
      </div>
    </>
  );
}
